import { IClientFormData, IClientGridRow, IClientPayload } from '@/Interfaces/Client.interface';
import ModalHeaderIcon from '@/components/Icons/ModalHeaderIcon';
import LoadingBackdropWrapper from '@/components/Modal/common/LoadingBackdropWrapper';
import ModalTitleCard from '@/components/Modal/common/ModalTitleCard';
import { DEFAULT_CLIENT } from '@/constants/clients';
import { prepareClientPayload } from '@/helpers/clients';
import ClientInfo from '@/pages/Clients/ClientModal/ClientInfo';
import { createClient, updateClient } from '@/store/api/clients.api';
import { Button, Dialog, DialogContent } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { toast } from 'react-toastify';

interface IProps {
  form: UseFormReturn<IClientFormData>;
  open: boolean;
  onClose: (refresh?: boolean) => void;
  selectedClient: IClientGridRow | null;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  openAdvertiserModal: () => void;
  openAgencyModal: () => void;
}

const ClientModal = ({
  form,
  open,
  onClose,
  selectedClient,
  loading,
  setLoading,
  openAdvertiserModal,
  openAgencyModal,
}: IProps) => {
  const { handleSubmit, reset } = form;

  useEffect(() => {
    if (selectedClient) {
      reset({
        ...selectedClient,
        newAgencies: [],
        newAdvertisers: [],
      });
    } else {
      reset(DEFAULT_CLIENT);
    }
  }, [selectedClient, reset]);

  const updateClientData = useCallback(
    async (id: number, payload: IClientPayload) => {
      setLoading(true);
      try {
        await updateClient(id, payload);
        toast.success('Client updated successfully');
      } catch (error) {
        toast.error((error as Error).message || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    },
    [setLoading, reset],
  );

  const createNewClient = useCallback(
    async (payload: IClientPayload) => {
      setLoading(true);
      try {
        await createClient(payload);
        toast.success('Client created successfully');
        reset();
      } catch (error) {
        toast.error((error as Error).message || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    },
    [setLoading, reset],
  );

  const onSubmit = useCallback(
    async ({
      id,
      name,
      externalId,
      whitelabel,
      advertisers,
      agencies,
      newAgencies,
      newAdvertisers,
    }: IClientFormData) => {
      const payload = prepareClientPayload({
        name,
        externalId: externalId?.trim() || null,
        newAgencies,
        newAdvertisers,
        whitelabelId: whitelabel!.value,
        advertiserIds: advertisers.map(advertiser => advertiser.value),
        agencyIds: agencies.map(agency => agency.value),
      });

      if (id && selectedClient) {
        await updateClientData(id, payload);
      } else {
        await createNewClient(payload);
      }
      onClose(true);
    },
    [createNewClient, updateClientData, selectedClient, onClose],
  );

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  return (
    <LoadingBackdropWrapper backdropOpen={loading}>
      <Dialog maxWidth={false} open={open} onClose={handleClose} aria-labelledby='modal-title'>
        <DialogContent sx={{ width: '650px', overflowX: 'hidden' }}>
          <ModalTitleCard
            titleIcon={<ModalHeaderIcon />}
            handleClose={handleClose}
            title={selectedClient ? 'View Client Details' : 'Create New Client'}
          />
          <FormProvider {...form}>
            <form className='mt-4' onSubmit={handleSubmit(onSubmit)}>
              <ClientInfo openAdvertiserModal={openAdvertiserModal} openAgencyModal={openAgencyModal} />
              <hr className='-mx-[32px] my-5' />
              <div className='mt-5 flex justify-end gap-[12px]'>
                <Button color='inherit' variant='outlined' onClick={handleClose}>
                  Cancel
                </Button>
                <Button color='primary' variant='contained' type='submit'>
                  {selectedClient ? 'Save' : 'Create'}
                </Button>
              </div>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>
    </LoadingBackdropWrapper>
  );
};

export default ClientModal;
